import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'
import { apiRequest } from '@/lib/api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { Clock } from 'lucide-react'
import type { TFunction } from 'i18next'

interface TermsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  type: 'platform' | 'business' | 'agent' | 'both' | 'agent-both'
  businessId?: string
  onAccepted?: () => void
}

interface LegalDocument {
  id: string
  type: string
  version: string
  locale: string
  content: string
  effective_at: string
}

interface LegalDocumentResponse {
  data: LegalDocument
  inactive?: boolean
}

/** Inject target="_blank" rel="noopener noreferrer" into all anchor tags */
function injectLinkTargets(html: string): string {
  return html.replace(/<a\b([^>]*)>/gi, (match, attrs: string) => {
    if (/\btarget\s*=/i.test(attrs)) return match
    const separator = attrs.trim().length > 0 && !attrs.endsWith(' ') ? ' ' : ''
    return `<a${attrs}${separator}target="_blank" rel="noopener noreferrer">`
  })
}

function LegalContent({ html }: { html: string }) {
  const processedHtml = useMemo(() => injectLinkTargets(html), [html])
  return (
    <div
      className="prose prose-sm max-w-none dark:prose-invert
        prose-headings:mt-6 prose-headings:mb-3
        prose-h2:text-lg prose-h2:font-semibold
        prose-p:my-0 prose-p:leading-7
        prose-ul:my-0 prose-ul:space-y-0
        prose-ol:my-0 prose-ol:space-y-0
        prose-li:my-0"
      dangerouslySetInnerHTML={{ __html: processedHtml }}
    />
  )
}

function ContentSkeleton() {
  return (
    <div className="space-y-4 py-4">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
    </div>
  )
}

function InactiveDocumentState({ t }: { t: TFunction<'translation', undefined> }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center space-y-4">
      <Clock className="h-12 w-12 text-muted-foreground" />
      <h2 className="text-xl font-semibold">
        {t('legal.inactive.title', 'Document being updated')}
      </h2>
      <p className="text-muted-foreground max-w-md">
        {t('legal.inactive.description', 'This legal document is currently being revised. Please check back later.')}
      </p>
    </div>
  )
}

function DocumentPanel({
  document: doc,
  isLoading,
  isInactive,
  locale,
  t,
}: {
  document?: LegalDocument
  isLoading: boolean
  isInactive: boolean
  locale: string
  t: TFunction<'translation', undefined>
}) {
  if (isLoading) {
    return <ContentSkeleton />
  }

  if (isInactive) {
    return <InactiveDocumentState t={t} />
  }

  if (doc) {
    return (
      <>
        <div className="flex-1 overflow-y-auto border rounded-md p-4">
          <LegalContent html={doc.content} />
        </div>
        <div className="text-xs text-muted-foreground pt-2 shrink-0">
          {t('legal.version', 'Version {{version}} — Effective {{date}}', {
            version: doc.version,
            date: new Date(doc.effective_at).toLocaleDateString(locale),
          })}
        </div>
      </>
    )
  }

  return (
    <Alert>
      <AlertDescription>
        {t('legal.notFound', 'Document not found.')}
      </AlertDescription>
    </Alert>
  )
}

export function TermsAcceptanceModal({
  open,
  onOpenChange,
  type,
  businessId,
  onAccepted,
}: TermsModalProps) {
  const { t, i18n } = useTranslation()
  const [platformAccepted, setPlatformAccepted] = useState(false)
  const [businessAccepted, setBusinessAccepted] = useState(false)
  const [agentAccepted, setAgentAccepted] = useState(false)
  const locale = i18n.language

  const isBusinessBoth = type === 'both'
  const isAgentBoth = type === 'agent-both'
  const showBoth = isBusinessBoth || isAgentBoth
  const showPlatform = showBoth || type === 'platform'
  const showBusiness = isBusinessBoth || type === 'business'
  const showAgent = isAgentBoth || type === 'agent'

  const { data: platformResponse, isLoading: platformLoading } = useQuery({
    queryKey: ['legal-document', 'platform_terms', locale],
    queryFn: async () => {
      return apiRequest<LegalDocumentResponse>(
        `/v1/legal-documents/platform_terms?locale=${locale}`
      )
    },
    enabled: open && showPlatform,
  })

  const { data: businessResponse, isLoading: businessLoading } = useQuery({
    queryKey: ['legal-document', 'business_terms', locale, businessId],
    queryFn: async () => {
      const params = new URLSearchParams({ locale })
      if (businessId) params.append('business_id', businessId)
      return apiRequest<LegalDocumentResponse>(
        `/v1/legal-documents/business_terms?${params.toString()}`
      )
    },
    enabled: open && showBusiness,
  })

  const { data: agentResponse, isLoading: agentLoading } = useQuery({
    queryKey: ['legal-document', 'agent_terms', locale],
    queryFn: async () => {
      return apiRequest<LegalDocumentResponse>(
        `/v1/legal-documents/agent_terms?locale=${locale}`
      )
    },
    enabled: open && showAgent,
  })

  const platformDocument = platformResponse?.data
  const platformInactive = platformResponse?.inactive === true

  const businessDocument = businessResponse?.data
  const businessInactive = businessResponse?.inactive === true

  const agentDocument = agentResponse?.data
  const agentInactive = agentResponse?.inactive === true

  const platformAcceptMutation = useMutation({
    mutationFn: async () => {
      return apiRequest<{ message: string; accepted_at: string; version: string }>(
        '/v1/terms/accept-platform',
        { method: 'POST' }
      )
    },
  })

  const businessAcceptMutation = useMutation({
    mutationFn: async () => {
      const body = businessId
        ? JSON.stringify({ business_id: businessId })
        : undefined
      return apiRequest<{ message: string; accepted_at: string; version: string }>(
        '/v1/terms/accept-business',
        {
          method: 'POST',
          headers: body ? { 'Content-Type': 'application/json' } : undefined,
          body,
        }
      )
    },
  })

  const agentAcceptMutation = useMutation({
    mutationFn: async () => {
      return apiRequest<{ message: string; accepted_at: string; version: string }>(
        '/v1/terms/accept-agent',
        { method: 'POST' }
      )
    },
  })

  const handleAccept = async () => {
    try {
      if (showPlatform) {
        await platformAcceptMutation.mutateAsync()
      }
      if (showBusiness) {
        await businessAcceptMutation.mutateAsync()
      }
      if (showAgent) {
        await agentAcceptMutation.mutateAsync()
      }
      onAccepted?.()
      onOpenChange(false)
    } catch {
      // Error handled by mutation state
    }
  }

  const hasError = platformAcceptMutation.isError || businessAcceptMutation.isError || agentAcceptMutation.isError
  const isPending = platformAcceptMutation.isPending || businessAcceptMutation.isPending || agentAcceptMutation.isPending

  const canAccept = showBoth
    ? platformAccepted && (showBusiness ? businessAccepted : showAgent ? agentAccepted : false)
    : showPlatform
      ? platformAccepted
      : showBusiness
        ? businessAccepted
        : showAgent
          ? agentAccepted
          : false

  const title = showBoth
    ? t('terms.both.title', 'Terms of Service')
    : showPlatform
      ? t('terms.platform.title', 'Platform Terms of Service')
      : showBusiness
        ? t('terms.business.title', 'Business Terms & Conditions')
        : t('terms.agent.title', 'Agent Terms of Service')

  const description = showBoth
    ? t('terms.both.description', 'Please review and accept both documents to continue.')
    : showPlatform
      ? t('terms.platform.description', 'Please review and accept the platform terms to continue.')
      : showBusiness
        ? t('terms.business.description', 'Please review and accept the business terms to continue.')
        : t('terms.agent.description', 'Please review and accept the agent terms to continue.')

  const singleDocument = showPlatform ? platformDocument : showBusiness ? businessDocument : agentDocument
  const singleLoading = showPlatform ? platformLoading : showBusiness ? businessLoading : agentLoading
  const singleInactive = showPlatform ? platformInactive : showBusiness ? businessInactive : agentInactive

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] md:w-[85vw] lg:w-[60vw] lg:max-w-[1200px] max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-xl">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {/* Scrollable body */}
        <div className="flex-1 min-h-0 flex flex-col px-6 overflow-hidden">
          <DocumentPanel
            document={singleDocument}
            isLoading={singleLoading}
            isInactive={singleInactive}
            locale={locale}
            t={t}
          />
        </div>

        {/* Footer: checkboxes + actions */}
        <div className="px-6 pb-6 pt-4 space-y-4">
          {hasError && (
            <Alert variant="destructive">
              <AlertDescription>
                {t('terms.error.accept', 'Failed to accept terms. Please try again.')}
              </AlertDescription>
            </Alert>
          )}

          {/* Checkboxes stacked vertically */}
          <div className="space-y-3">
            {showPlatform && (
              <div className="flex items-start gap-3">
                <Checkbox
                  id="platform-terms-accept"
                  checked={platformAccepted}
                  onCheckedChange={(checked) => setPlatformAccepted(checked === true)}
                  className="mt-0.5"
                />
                <label htmlFor="platform-terms-accept" className="text-sm font-normal cursor-pointer leading-5">
                  {t('terms.platform.acceptLabel', 'I agree to the')}{' '}
                  <a
                    href="/legal/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline underline-offset-2 hover:text-primary/80"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('terms.platform.termsLink', 'Terms of Service')}
                  </a>
                  {', '}
                  <a
                    href="/legal/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline underline-offset-2 hover:text-primary/80"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('terms.platform.privacyLink', 'Privacy Policy')}
                  </a>
                  {' '}{t('terms.and', 'and')}{' '}
                  <a
                    href="/legal/cookies"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline underline-offset-2 hover:text-primary/80"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('terms.platform.cookiesLink', 'Cookie Policy')}
                  </a>
                  {'.'}
                </label>
              </div>
            )}

            {showBusiness && (
              <div className="flex items-start gap-3">
                <Checkbox
                  id="business-terms-accept"
                  checked={businessAccepted}
                  onCheckedChange={(checked) => setBusinessAccepted(checked === true)}
                  className="mt-0.5"
                />
                <label htmlFor="business-terms-accept" className="text-sm font-normal cursor-pointer leading-5">
                  {t('terms.business.acceptLabel', 'I agree to the')}{' '}
                  <a
                    href="/legal/business_terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline underline-offset-2 hover:text-primary/80"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('terms.business.termsLink', 'Business Terms & Conditions')}
                  </a>
                  {' '}{t('terms.and', 'and')}{' '}
                  <a
                    href="/legal/business_privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline underline-offset-2 hover:text-primary/80"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('terms.business.privacyLink', 'Business Privacy Policy')}
                  </a>
                  {'.'}
                </label>
              </div>
            )}

            {showAgent && (
              <div className="flex items-start gap-3">
                <Checkbox
                  id="agent-terms-accept"
                  checked={agentAccepted}
                  onCheckedChange={(checked) => setAgentAccepted(checked === true)}
                  className="mt-0.5"
                />
                <label htmlFor="agent-terms-accept" className="text-sm font-normal cursor-pointer leading-5">
                  {t('terms.agent.acceptLabel', 'I agree to the')}{' '}
                  <a
                    href="/legal/agent_terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline underline-offset-2 hover:text-primary/80"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('terms.agent.termsLink', 'Agent Terms of Service')}
                  </a>
                  {' '}{t('terms.and', 'and')}{' '}
                  <a
                    href="/legal/agent_privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline underline-offset-2 hover:text-primary/80"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('terms.agent.privacyLink', 'Agent Privacy Policy')}
                  </a>
                  {'.'}
                </label>
              </div>
            )}
          </div>

          <DialogFooter className="flex-row gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 sm:flex-none"
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleAccept}
              disabled={!canAccept || isPending}
              className="flex-1 sm:flex-none"
            >
              {isPending
                ? t('common.processing', 'Processing...')
                : t('terms.accept', 'Accept & Continue')
              }
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
